import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Pagination, Tabs } from 'antd';
import Empty from 'components/Empty';
import useModal from 'hooks/useModal';
import { useTranslate } from '@tolgee/react';
import { QueryReturnListStateCountV2Resp, SecondaryProcessStatus } from 'services/api/refund';
import { FilterChangeEvent } from '../components/OrderFilter/Filter';
import LazadaReturn from '../components/LazadaReturn';
import RefundDrawer from '../components/RefundDrawer';
import useFilter from '../hooks/useFilter';
import useReturnList from '../hooks/useReturnList';
import { getCount } from '../utils/count';
import { useMemoizedFn } from 'ahooks';
import { FilterIdType } from '../utils/constants';
import { ExportButton } from '../components/ExportButton';
import { ShopeeReturnFilter } from '../components/OrderFilter/ShopeeReturnFilter';

const Container = styled.div`
  flex: 1 0 0 !important;
  padding: 16px;
  .ant-tabs {
    position: sticky;
    background: #fff;
    border-radius: 2px;
    z-index: 1;
  }
  .ant-tabs {
    top: 0;
    .ant-tabs-nav {
      margin-bottom: 0;
    }
  }
  .ant-pagination {
    position: sticky;
    bottom: 0;
    background: #fff;
    border-radius: 2px;
    padding: 8px 24px;
    text-align: right;
  }

  > * + * {
    margin-top: 12px;
  }
`;

function LazadaReturnList() {
  const { t } = useTranslate();

  const { query, onQueryChange } = useFilter();

  const {
    list = [],
    statusFilter,
    setStatusFilter,
    loading,
    statusCount,
    pagination: { pageSize, current, onChange, total },
    refresh,
    conversationList = [],
  } = useReturnList(query, 'LAZADA', 'REQUEST');

  const { visible, hide, meta: activeIndex = -1 } = useModal<number>();

  const activeReturn = list[activeIndex];

  const activeConversation = conversationList[activeIndex];

  const handleFilterChange = useMemoizedFn((e: FilterChangeEvent) => {
    const { snType, sn } = e;
    const { orderSn, returnSn, returnTrackingNumber } = query;
    if (
      (snType === FilterIdType.orderId && sn !== orderSn) ||
      (snType === FilterIdType.returnId && sn !== returnSn) ||
      (snType === FilterIdType.returnTrackingNumber && sn !== returnTrackingNumber)
    ) {
      setStatusFilter('ALL');
    }

    onQueryChange(e);
  });

  const filter = <ShopeeReturnFilter onChange={handleFilterChange} filterType="return" />;

  const statusLabelMap = {
    ALL: t('orderStatus.ALL'),
    REQUEST: t('orderStatusShopee.REQUEST'),
    PROCESS: t('ViewRefund.退货中'),
    REFUND: t('shopee.REFUND_PAID'),
    COMPLETE: t('feedback.已关闭'),
    REFUSE: t('ViewRefund.已拒绝'),
  };

  const statusCountField: Partial<
    Record<keyof typeof statusLabelMap, keyof QueryReturnListStateCountV2Resp>
  > = {
    REQUEST: 'pendingCount',
  };

  const tabs = (
    <Tabs
      activeKey={statusFilter}
      onChange={(key: any) => setStatusFilter(key)}
      tabBarExtraContent={
        <div className="flex px-4 gap-4">
          <ExportButton
            statusFilter={statusFilter}
            query={query}
            platform="LAZADA"
            secondaryProcessStatus={SecondaryProcessStatus.ALL}
          />
        </div>
      }
    >
      {Object.keys(statusLabelMap).map((key) => {
        const { [key]: label } = statusLabelMap;

        const { [key]: countField } = statusCountField;

        if (!statusCount || !countField) {
          return <Tabs.TabPane key={key} tab={label} />;
        }

        const { [countField]: count } = statusCount;

        return <Tabs.TabPane key={key} tab={`${label}(${getCount(count)})`} />;
      })}
    </Tabs>
  );

  const content = (() => {
    if (list.length === 0) {
      if (loading) {
        return <LoadingOutlined />;
      }

      return <Empty />;
    }

    return list.map((returnOrder, index) => {
      const { returnSn } = returnOrder;

      return (
        <LazadaReturn
          key={returnSn}
          returnOrder={returnOrder}
          conversation={conversationList[index]}
          onRefresh={refresh}
        />
      );
    });
  })();

  const page = list.length > 0 && (
    <Pagination
      current={current}
      pageSize={pageSize}
      onChange={onChange}
      total={total}
      showSizeChanger
      pageSizeOptions={['10', '20']}
    />
  );

  const returnDrawer = activeReturn !== undefined && (
    <RefundDrawer
      platform="lazada"
      visible={visible}
      onUpdate={refresh}
      onClose={() => hide()}
      type="refund"
      row={activeReturn}
      conversation={activeConversation}
      refreshList={refresh}
    />
  );

  return (
    <>
      {filter}
      <Container>
        {tabs}
        {content}
        {page}
        {returnDrawer}
      </Container>
    </>
  );
}

export default LazadaReturnList;
